import { useEffect, useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { UserPlus, KeyRound, CheckCircle2, ArrowRight } from "lucide-react";
import { AccentButton, AuthScene, Button, Input, Label, Loader } from "@wa/ui";
import type { AuthLayoutSpec } from "@wa/core/theme";
import { useT } from "../i18n/I18nProvider";
import { acceptInvite, inviteStatus, isExistingAccount, type AccountUser, type InviteStatus } from "../lib/auth";
import { Logo } from "../components/Logo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";

/**
 * Client portal invite acceptance page - the target of the invite email link (`/accept-invite?token=…`).
 * A client without an account creates one and is signed straight into the portal. If the email
 * already has an account, they are asked to sign in normally.
 */
export function AcceptInvitePage({
  onSuccess,
  onLogin,
  authSpec,
}: {
  onSuccess: (u: AccountUser) => void;
  onLogin: () => void;
  authSpec?: AuthLayoutSpec;
}) {
  const t = useT();
  const glow = authSpec?.glow ?? "aurora";

  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [status, setStatus] = useState<"checking" | "invalid" | "existing" | "form">("checking");
  const [info, setInfo] = useState<InviteStatus | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    inviteStatus(token).then((s) => {
      if (!s.valid) {
        setStatus("invalid");
        return;
      }
      setInfo(s);
      setName(s.name ?? "");
      setStatus(s.existingAccount ? "existing" : "form");
    });
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr(t("invite.tooShort"));
      return;
    }
    setBusy(true);
    try {
      const res = await acceptInvite(token, name.trim(), password);
      if (isExistingAccount(res)) setStatus("existing");
      else onSuccess(res);
    } catch (e) {
      const code = e instanceof Error ? e.message : "";
      if (code === "invalid_or_expired") setStatus("invalid");
      else setErr(t("invite.error"));
    } finally {
      setBusy(false);
    }
  }

  const company = info?.companyName ?? "";

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={<LanguageMenu />}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="card-aurora-border rounded-xl bg-card/95 p-6 shadow-soft">
          {status === "checking" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Loader size="md" />
              <p className="text-sm text-muted-foreground">{t("invite.checking")}</p>
            </div>
          ) : status === "invalid" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <KeyRound className="size-8 text-destructive" />
              <h1 className="text-xl font-bold tracking-tight">{t("invite.title")}</h1>
              <p className="text-sm text-destructive">{t("invite.invalid")}</p>
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={onLogin}>
                {t("invite.toLogin")}
              </Button>
            </div>
          ) : status === "existing" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <CheckCircle2 className="size-8 text-primary" />
              <h1 className="text-xl font-bold tracking-tight">{t("invite.existingTitle")}</h1>
              <p className="text-sm text-muted-foreground">{t("invite.existing", { email: info?.email ?? "" })}</p>
              <AccentButton glow={glow} type="button" size="lg" className="w-full" onClick={onLogin}>
                {t("invite.toLogin")}
                <ArrowRight className="ml-1 size-4" />
              </AccentButton>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <UserPlus className="size-8 text-primary" />
                <h1 className="text-xl font-bold tracking-tight">{t("invite.title")}</h1>
                <p className="text-sm text-muted-foreground">
                  {company ? t("invite.subtitle", { company }) : t("invite.subtitleGeneric")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-email">{t("invite.email")}</Label>
                <Input id="invite-email" type="email" value={info?.email ?? ""} readOnly disabled />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-name">{t("invite.name")}</Label>
                <Input id="invite-name" type="text" autoComplete="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-password">{t("invite.password")}</Label>
                <Input id="invite-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {err && <p className="text-sm text-destructive">{err}</p>}
              <AccentButton glow={glow} type="submit" size="lg" loading={busy} disabled={!name.trim()} className="w-full">
                {busy ? t("invite.busy") : t("invite.submit")}
              </AccentButton>
              <button type="button" onClick={onLogin} className="self-center text-xs font-medium text-muted-foreground hover:text-primary hover:underline">
                {t("invite.haveAccount")}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </AuthScene>
  );
}
